import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, Typography, TextField, Button, Table, TableHead, TableRow,
  TableCell, TableBody, IconButton, Chip, Grid, Pagination, Avatar, Paper, Stack, Tooltip, CircularProgress, Divider, MenuItem, InputAdornment, CardContent
} from "@mui/material";
import {
  EditOutlined, DeleteOutline, PersonAddAlt1, CloudUpload, Delete, CheckCircle, Search, MailOutline, LocalPhone,
  DescriptionOutlined, AddCircleOutline, LocationOnOutlined, BadgeOutlined, VpnKeyOutlined
} from "@mui/icons-material";
import {
  collection, getDocs, doc, setDoc, deleteDoc, query, where, Timestamp, updateDoc, arrayUnion, arrayRemove, orderBy
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { db, storage } from "../../firebase";
import Swal from "sweetalert2";

const PAGE_SIZE = 20;
const CONTACT_TYPES = ["Father", "Mother", "Husband", "Wife", "Local Guardian", "Other"];

function Canteensmgmt() {
  const auth = getAuth();
  const [users, setUsers] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [schoolId, setSchoolId] = useState(null);
  const [uploadingMap, setUploadingMap] = useState({});

  const [form, setForm] = useState({
    name: "", email: "", mobile: "", password: "", role: "Canteen", status: "Active",
    address: "", city: "", state: "", pincode: "",
    documents: [], contacts: [{ type: "Father", number: "" }]
  });

  useEffect(() => {
    const initData = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const q = query(collection(db, "users"), where("uid", "==", currentUser.uid));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const sId = snap.docs[0].data().schoolId;
        setSchoolId(sId);
        loadUsers(sId);
      }
      const docSnap = await getDocs(query(collection(db, "staff_documents"), orderBy("title")));
      setDocTypes(docSnap.docs.map(d => d.data().title));
    };
    initData();
  }, []);

  const loadUsers = async (sId) => {
    const q = query(collection(db, "users"), where("schoolId", "==", sId), where("role", "==", "Canteen"));
    const snap = await getDocs(q);
    setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u => u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()));
  }, [users, search]);

  const paginatedUsers = filteredUsers?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleContactChange = (index, field, value) => {
    const updated = [...form.contacts];
    updated[index][field] = value;
    setForm({ ...form, contacts: updated });
  };

  const handleFileUpload = async (e, typeTitle) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMap(prev => ({ ...prev, [typeTitle]: true }));
    try {
      const fileRef = ref(storage, `schools/${schoolId}/Canteens/${editingId || 'temp'}/${Date.now()}_${typeTitle}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      const newDoc = { name: file.name, url, docType: typeTitle, uploadedAt: new Date() };

      if (editingId) await updateDoc(doc(db, "users", editingId), { documents: arrayUnion(newDoc) });
      setForm(prev => ({ ...prev, documents: [...prev.documents.filter(d => d.docType !== typeTitle), newDoc] }));
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `${typeTitle} Uploaded`, showConfirmButton: false, timer: 2000 });
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setUploadingMap(prev => ({ ...prev, [typeTitle]: false }));
  };

  const saveUser = async () => {
    if (!form.name || !form.email) return Swal.fire("Required", "Please fill Name and Email", "warning");
    try {
      let uid = editingId;
      if (!editingId) {
        const user = await createUserWithEmailAndPassword(auth, form.email, form.password);
        uid = user.user.uid;
      }
      await setDoc(doc(db, "users", uid), { uid, ...form, schoolId, createdTime: editingId ? form.createdTime : Timestamp.now() });
      Swal.fire("Success", "Canteen Staff database updated", "success");
      resetForm();
      loadUsers(schoolId);
    } catch (err) { Swal.fire("Error", err.message, "error"); }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", mobile: "", password: "", role: "Canteen", status: "Active", address: "", city: "", state: "", pincode: "", documents: [], contacts: [{ type: "Father", number: "" }] });
    setEditingId(null);
  };

  const deleteUser = async (id) => {
    if (!(await Swal.fire({ title: "Are you sure?", text: "This will permanently delete the staff record.", icon: "warning", showCancelButton: true, confirmButtonColor: '#d33' })).isConfirmed) return;
    await deleteDoc(doc(db, "users", id));
    loadUsers(schoolId);
  };

  return (
    <Box sx={{ bgcolor: "#f4f7fe", minHeight: "100vh" }}>
      <Grid className="row" spacing={3}>

        {/* Left Column: Form Section */}
        <Grid  className="col-sm-4"  item xs={12} lg={4}>
          <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", border: "1px solid #eef2f6" }}>
            <CardContent sx={{ p: 2 }}> {/* Reduced padding from 3 to 2 */}
              <Stack spacing={1.5}> {/* Tighter vertical spacing */}

                {/* Basic Information */}
                <Box>
                  <Typography variant="caption" color="primary" fontWeight={700} sx={{ ml: 0.5, mb: 1, display: 'block' }}>
                    BASIC INFORMATION
                  </Typography>
                  <Stack spacing={1}>
                    <TextField fullWidth size="small" label="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start"><BadgeOutlined sx={{ fontSize: 18 }} /></InputAdornment>, sx: { borderRadius: 1.5 } }}
                    />
                    <TextField fullWidth size="small" label="Official Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start"><MailOutline sx={{ fontSize: 18 }} /></InputAdornment>, sx: { borderRadius: 1.5 } }}
                    />
                    <TextField fullWidth size="small" label="Personal Phone" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start"><LocalPhone sx={{ fontSize: 18 }} /></InputAdornment>, sx: { borderRadius: 1.5 } }}
                    />
                    {!editingId && (
                      <TextField fullWidth size="small" type="password" label="Login Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start"><VpnKeyOutlined sx={{ fontSize: 18 }} /></InputAdornment>, sx: { borderRadius: 1.5 } }}
                      />
                    )}
                  </Stack>
                </Box>

                {/* Address Section */}
                <Box>
                  <Typography variant="caption" color="primary" fontWeight={700} sx={{ ml: 0.5, mb: 1, display: 'block' }}>
                    CURRENT ADDRESS
                  </Typography>
                  <Stack spacing={1}>
                    <TextField fullWidth size="small" multiline rows={1} label="Street Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                      InputProps={{ sx: { borderRadius: 1.5 } }}
                    />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField fullWidth label="City" size="small" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} InputProps={{ sx: { borderRadius: 1.5 } }} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth label="Pincode" size="small" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} InputProps={{ sx: { borderRadius: 1.5 } }} />
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>

                {/* Contacts Section */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="primary" fontWeight={700} sx={{ ml: 0.5 }}>EMERGENCY CONTACTS</Typography>
                    <IconButton size="small" color="primary" onClick={() => setForm({ ...form, contacts: [...form.contacts, { type: "Other", number: "" }] })} sx={{ p: 0.5 }}>
                      <AddCircleOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                  <Stack spacing={1} sx={{ mt: 0.5 }}>
                    {form.contacts.map((c, i) => (
                      <Stack direction="row" spacing={0.5} key={i} alignItems="center">
                        <TextField select size="small" sx={{ width: 200, "& .MuiInputBase-root": { fontSize: 13 } }} value={c.type} onChange={(e) => handleContactChange(i, "type", e.target.value)}>
                          {CONTACT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                        </TextField>
                        <TextField fullWidth size="small" placeholder="Number" value={c.number} onChange={(e) => handleContactChange(i, "number", e.target.value)} InputProps={{ sx: { borderRadius: 1.5, fontSize: 12 } }} />
                        <IconButton color="error" size="small" onClick={() => setForm({ ...form, contacts: form.contacts.filter((_, idx) => idx !== i) })}>
                          <DeleteOutline sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                {/* Documents Section */}
                <Box>
                  <Typography variant="caption" color="primary" fontWeight={700} sx={{ ml: 0.5 }}>DOCUMENTS</Typography>
                  <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                    {docTypes.map((title) => {
                      const uploaded = form.documents?.find(d => d.docType === title);
                      return (
                        <Grid item xs={12} key={title}>
                          <Paper variant="outlined" sx={{ px: 1, py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2, bgcolor: uploaded ? '#f6fff8' : 'transparent' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {uploaded ? <CheckCircle sx={{ fontSize: 16 }} color="success" /> : <DescriptionOutlined sx={{ fontSize: 16 }} color="disabled" />}
                              <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{title}</Typography>
                            </Stack>
                            <Button component="label" size="small" sx={{ fontSize: 10, minWidth: 0, p: 0.5 }}>
                              {uploadingMap[title] ? <CircularProgress size={12} /> : (uploaded ? "Change" : "Upload")}
                              <input hidden type="file" onChange={(e) => handleFileUpload(e, title)} />
                            </Button>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                {/* Submit Actions */}
                <Stack spacing={1} sx={{ pt: 1 }}>
                  <Button variant="contained" fullWidth onClick={saveUser} sx={{ borderRadius: 2, py: 1, fontSize: 13, fontWeight: 700 }}>
                    {editingId ? "Update Record" : "Confirm Save"}
                  </Button>
                  {editingId && (
                    <Button variant="outlined" color="inherit" size="small" onClick={resetForm} sx={{ borderRadius: 2, fontSize: 11 }}>
                      Cancel Edit
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Table Section */}
        <Grid className="col-sm-8" item xs={12} lg={7.5}>
          <Paper sx={{ borderRadius: 5, boxShadow: "0 10px 40px rgba(0,0,0,0.03)", border: "1px solid #eef2f6", overflow: "hidden" }}>
            <Box sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={800}>Canteens Staff</Typography>
                <Typography variant="caption" color="text.secondary">Manage and monitor school faculty</Typography>
              </Box>
              <TextField
                placeholder="Search by name or email..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: { xs: '100%', sm: 260 }, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fcfdfe' } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Box>

            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafd" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Sr No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Primary Mobile</TableCell>
                  {/* <TableCell sx={{ fontWeight: 700 }}>Contacts</TableCell> */}
                  <TableCell sx={{ fontWeight: 700 }}>Adress</TableCell>
                  {/* <TableCell sx={{ fontWeight: 700 }}>State</TableCell> */}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedUsers.map((u, index) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Typography fontSize={12} color="text.secondary">
                        {(page - 1) * PAGE_SIZE + index + 1}
                      </Typography>
                    </TableCell>
                    {/* NAME */}
                    <TableCell sx={{ width: 300 }}>
                      {u.name}
                    </TableCell>

                    {/* EMAIL */}
                    <TableCell>
                      <Typography fontSize={13}>{u.email}</Typography>
                    </TableCell>

                    {/* PRIMARY MOBILE */}
                    <TableCell>
                      <Typography fontSize={13} fontWeight={600}>
                        {u.mobile || "-"}
                      </Typography>
                    </TableCell>

                    {/* CONTACTS */}
                    {/* <TableCell>
                      {u.contacts && u.contacts.length > 0 ? (
                        u.contacts.map((c, i) => (
                          <Typography key={i} fontSize={12}>
                            <b>{c.type}:</b> {c.number}
                          </Typography>
                        ))
                      ) : (
                        <Typography fontSize={12} color="text.secondary">
                          No Contacts
                        </Typography>
                      )}
                    </TableCell> */}

                    {/* CITY */}
                    <TableCell>
                      <Typography fontSize={13}>{u.city || "-"}</Typography>
                    </TableCell>

                    {/* STATE */}
                    {/* <TableCell>
                      <Typography fontSize={13}>{u.state || "-"}</Typography>
                    </TableCell> */}

                    {/* ACTIONS */}
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>

                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setForm({
                                ...u,
                                contacts: u.contacts || [{ type: "Father", number: "" }]
                              });
                              setEditingId(u.id);
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteUser(u.id)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>

                      </Stack>
                    </TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', borderTop: '1px solid #f1f1f1' }}>
              <Pagination count={Math.ceil(filteredUsers.length / PAGE_SIZE)} page={page} onChange={(e, v) => setPage(v)} sx={{ '& .MuiPaginationItem-root': { borderRadius: 2, fontWeight: 700 } }} color="primary" />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Canteensmgmt;